// apps/accounts/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import Login from './pages/Login';
import PartnerSignup from './pages/PartnerSignup';

// ─── Partner Routes ──────────────────────────────────────────
import PartnerLayout from './layouts/PartnerLayout';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerProjects from './pages/partner/PartnerProjects';
import PartnerProjectDetail from './pages/partner/PartnerProjectDetail';
import PartnerAttendance from './pages/partner/PartnerAttendance';
import PartnerExpenses from './pages/partner/PartnerExpenses';
import PartnerVendorPayments from './pages/partner/PartnerVendorPayments';
import PartnerClientPayments from './pages/partner/PartnerClientPayments';
import PartnerInvestments from './pages/partner/PartnerInvestments';
import PartnerReports from './pages/partner/PartnerReports';
import PartnerSettings from './pages/partner/PartnerSettings';

// ─── Staff Routes ─────────────────────────────────────────────
import StaffLayout from './layouts/StaffLayout';
import StaffDashboard from './pages/staff/StaffDashboard';
import StaffAttendance from './pages/staff/StaffAttendance';
import StaffMaterials from './pages/staff/StaffMaterials';
import StaffStock from './pages/staff/StaffStock';

// ─── Admin Staff Management ──────────────────────────────────
import StaffAttendanceAdmin from './pages/admin/StaffAttendanceAdmin';
import StaffLeaveManagement from './pages/admin/StaffLeaveManagement';
import StaffSalary from './pages/admin/StaffSalary';
import StaffAdvance from './pages/admin/StaffAdvance';

// ─── Dashboards ──────────────────────────────────────────────
import MainDashboard from './pages/dashboard/MainDashboard';
import ConstructionDashboard from './pages/dashboard/ConstructionDashboard';
import InteriorDashboard from './pages/dashboard/InteriorDashboard';

// ─── Core Modules ────────────────────────────────────────────
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Partners from './pages/Partners';
import Vendors from './pages/Vendors';
import ProjectExpenses from './pages/ProjectExpenses';
import VendorPayments from './pages/VendorPayments';
import ClientPayments from './pages/ClientPayments';
import Investments from './pages/Investments';
import Reports from './pages/Reports';
import Labour from './pages/Labour';
import Attendance from './pages/Attendance';

// ─── Materials Management ─────────────────────────────────────
import Materials from './pages/materials/Materials';
import Categories from './pages/materials/Categories';
import UOMs from './pages/materials/UOMs';
import Suppliers from './pages/materials/Suppliers';
import MaterialRequests from './pages/materials/MaterialRequests';
import MaterialRequestDetail from './pages/materials/MaterialRequestDetail';
import PurchaseOrders from './pages/materials/PurchaseOrders';
import PurchaseOrderDetail from './pages/materials/PurchaseOrderDetail';

// ─── Partner & Site Management ───────────────────────────────
import PartnerSiteManagement from './pages/PartnerSiteManagement';
import SiteEmployees from './pages/SiteEmployees';

// ─── Measurements & Billing ──────────────────────────────────
import WorkCategories from './pages/WorkCategories';
import ProjectVendorRates from './pages/ProjectVendorRates';
import SiteMeasurements from './pages/SiteMeasurements';
import SiteBills from './pages/SiteBills';

// ─── Labour Bills (New) ──────────────────────────────────────
import LabourBills from './pages/LabourBills';

// ─── Settings ─────────────────────────────────────────────────
import Settings from './pages/Settings';

// ─── Query Client ─────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// ─── Role-based Default Route ────────────────────────────────
const getDefaultPath = (role) => {
  if (role === 'partner-construction' || role === 'partner-interior' || role === 'client') {
    return '/partner/dashboard';
  }
  if (role === 'site_staff') {
    return '/staff/dashboard';
  }
  if (role === 'admin' || role === 'accounts') {
    return '/dashboard/main';
  }
  return '/dashboard/main';
};

// ─── Main App Routes ──────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();
  const role = user?.role || 'user';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        <span className="ml-2 text-slate-500">Loading...</span>
      </div>
    );
  }

  const defaultPath = getDefaultPath(role);
  const isPartner = role === 'partner-construction' || role === 'partner-interior' || role === 'client';
  const isStaff = role === 'site_staff';

  return (
    <Routes>
      {/* ─── Public Routes ─── */}
      <Route path="/login" element={<Login />} />
      <Route path="/partner-signup" element={<PartnerSignup />} />

      {/* ─── Partner Routes ─── */}
      <Route path="/partner" element={<PartnerLayout />}>
        <Route index element={<Navigate to="/partner/dashboard" replace />} />
        <Route path="dashboard" element={<PartnerDashboard />} />
        <Route path="projects" element={<PartnerProjects />} />
        <Route path="projects/:id" element={<PartnerProjectDetail />} />
        <Route path="attendance" element={<PartnerAttendance />} />
        <Route path="expenses" element={<PartnerExpenses />} />
        <Route path="vendor-payments" element={<PartnerVendorPayments />} />
        <Route path="client-payments" element={<PartnerClientPayments />} />
        <Route path="investments" element={<PartnerInvestments />} />
        <Route path="reports" element={<PartnerReports />} />
        <Route path="settings" element={<PartnerSettings />} />
      </Route>

      {/* ─── Staff Routes ─── */}
      <Route path="/staff" element={<StaffLayout />}>
        <Route index element={<Navigate to="/staff/dashboard" replace />} />
        <Route path="dashboard" element={<StaffDashboard />} />
        <Route path="attendance" element={<StaffAttendance />} />
        <Route path="materials" element={<StaffMaterials />} />
        <Route path="stock" element={<StaffStock />} />
      </Route>

      {/* ─── Block partner/staff from accessing admin routes ─── */}
      {isPartner && (
        <Route path="*" element={<Navigate to="/partner/dashboard" replace />} />
      )}
      {isStaff && (
        <Route path="*" element={<Navigate to="/staff/dashboard" replace />} />
      )}

      {/* ─── Admin/Accounts Routes (with Main Layout) ─── */}
      <Route path="/" element={<Layout />}>
        <Route index element={<Navigate to={defaultPath} replace />} />

        {/* ===== DASHBOARDS ===== */}
        <Route
          path="dashboard/main"
          element={
            <ProtectedRoute path="/dashboard/main">
              <MainDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard/construction"
          element={
            <ProtectedRoute path="/dashboard/construction">
              <ConstructionDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="dashboard/interior"
          element={
            <ProtectedRoute path="/dashboard/interior">
              <InteriorDashboard />
            </ProtectedRoute>
          }
        />

        {/* ===== PARTNER MANAGEMENT ===== */}
        <Route
          path="partner-site-management"
          element={
            <ProtectedRoute path="/partner-site-management">
              <PartnerSiteManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="site-employees"
          element={
            <ProtectedRoute path="/site-employees">
              <SiteEmployees />
            </ProtectedRoute>
          }
        />

        {/* ===== STAFF MANAGEMENT ===== */}
        <Route
          path="staff/attendance"
          element={
            <ProtectedRoute path="/staff/attendance">
              <StaffAttendanceAdmin />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff/leave"
          element={
            <ProtectedRoute path="/staff/leave">
              <StaffLeaveManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff/salary"
          element={
            <ProtectedRoute path="/staff/salary">
              <StaffSalary />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff/advance"
          element={
            <ProtectedRoute path="/staff/advance">
              <StaffAdvance />
            </ProtectedRoute>
          }
        />

        {/* ===== MEASUREMENTS & BILLING ===== */}
        <Route
          path="measurements/categories"
          element={
            <ProtectedRoute path="/measurements/categories">
              <WorkCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path="measurements/rates"
          element={
            <ProtectedRoute path="/measurements/rates">
              <ProjectVendorRates />
            </ProtectedRoute>
          }
        />
        <Route
          path="measurements"
          element={
            <ProtectedRoute path="/measurements">
              <SiteMeasurements />
            </ProtectedRoute>
          }
        />
        <Route
          path="measurements/bills"
          element={
            <ProtectedRoute path="/measurements/bills">
              <SiteBills />
            </ProtectedRoute>
          }
        />

        {/* ===== CORE MODULES ===== */}
        <Route
          path="projects"
          element={
            <ProtectedRoute path="/projects">
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route
          path="projects/:id"
          element={
            <ProtectedRoute path="/projects/:id">
              <ProjectDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="partners"
          element={
            <ProtectedRoute path="/partners">
              <Partners />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute path="/vendors">
              <Vendors />
            </ProtectedRoute>
          }
        />
        <Route
          path="labour"
          element={
            <ProtectedRoute path="/labour">
              <Labour />
            </ProtectedRoute>
          }
        />
        <Route
          path="attendance"
          element={
            <ProtectedRoute path="/attendance">
              <Attendance />
            </ProtectedRoute>
          }
        />

        {/* ===== LABOUR BILLS (NEW) ===== */}
        <Route
          path="labour-bills"
          element={
            <ProtectedRoute path="/labour-bills">
              <LabourBills />
            </ProtectedRoute>
          }
        />

        {/* ===== MATERIALS MANAGEMENT ===== */}
        <Route
          path="materials"
          element={
            <ProtectedRoute path="/materials">
              <Materials />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/categories"
          element={
            <ProtectedRoute path="/materials/categories">
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/uoms"
          element={
            <ProtectedRoute path="/materials/uoms">
              <UOMs />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/suppliers"
          element={
            <ProtectedRoute path="/materials/suppliers">
              <Suppliers />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/requests"
          element={
            <ProtectedRoute path="/materials/requests">
              <MaterialRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/requests/:id"
          element={
            <ProtectedRoute path="/materials/requests/:id">
              <MaterialRequestDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/purchase-orders"
          element={
            <ProtectedRoute path="/materials/purchase-orders">
              <PurchaseOrders />
            </ProtectedRoute>
          }
        />
        <Route
          path="materials/purchase-orders/:id"
          element={
            <ProtectedRoute path="/materials/purchase-orders/:id">
              <PurchaseOrderDetail />
            </ProtectedRoute>
          }
        />

        {/* ===== EXPENSES & PAYMENTS ===== */}
        <Route
          path="expenses"
          element={
            <ProtectedRoute path="/expenses">
              <ProjectExpenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendor-payments"
          element={
            <ProtectedRoute path="/vendor-payments">
              <VendorPayments />
            </ProtectedRoute>
          }
        />
        <Route
          path="client-payments"
          element={
            <ProtectedRoute path="/client-payments">
              <ClientPayments />
            </ProtectedRoute>
          }
        />
        <Route
          path="investments"
          element={
            <ProtectedRoute path="/investments">
              <Investments />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute path="/reports">
              <Reports />
            </ProtectedRoute>
          }
        />

        {/* ===== SETTINGS ===== */}
        <Route
          path="settings"
          element={
            <ProtectedRoute path="/settings">
              <Settings />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* ─── 404 Fallback ─── */}
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </Routes>
  );
}

// ─── Main App ────────────────────────────────────────────────────
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            richColors
            closeButton
            toastOptions={{
              duration: 4000,
              style: {
                fontSize: '14px',
                borderRadius: '12px',
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;